// ═══════════════════════════════════
// Claude AI Service Layer
// ═══════════════════════════════════
// Handles all Anthropic API calls for lead scoring,
// email drafting, company analysis, and campaign generation.

const API_URL = '/api/ai'; // Proxied through Netlify functions or Railway

async function callClaude(prompt) {
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });

  if (!response.ok) {
    throw new Error(`AI request failed: ${response.status}`);
  }

  const data = await response.json();
  return data;
}

export async function scoreLead(contact) {
  const prompt = `You are an expert sales lead scoring analyst. Score this lead on a scale of 1-100 and provide a brief analysis. Return ONLY valid JSON with keys: score (number), grade (A/B/C/D/F), summary (string ~40 words), signals (array of 3 key positive signals), risks (array of 1-2 risks), next_action (string ~15 words).

Lead Data:
- Name: ${contact.first_name} ${contact.last_name}
- Title: ${contact.title || 'Unknown'}
- Company: ${contact.organization_name || 'Unknown'}
- Email: ${contact.email || 'N/A'}
- Location: ${contact.present_raw_address || 'Unknown'}
- Seniority: ${contact.seniority || 'Unknown'}

Return valid JSON only, no markdown.`;

  return callClaude(prompt);
}

export async function draftEmail(contact, purpose = 'introduction', senderName = 'Lionel Mosley') {
  const prompt = `You are an expert B2B sales copywriter. Draft a compelling cold outreach email. Return ONLY valid JSON with keys: subject (string), body (string with \\n for line breaks), cta (string ~10 words), tone (string), personalization_hooks (array of 2 strings explaining why this is personalized).

Context:
- Sender: ${senderName}, IT Consultant
- Recipient: ${contact.first_name} ${contact.last_name}
- Recipient Title: ${contact.title || 'Unknown'}
- Recipient Company: ${contact.organization_name || 'Unknown'}
- Purpose: ${purpose}
- Keep it under 120 words, professional but warm.

Return valid JSON only, no markdown.`;

  return callClaude(prompt);
}

export async function analyzeCompany(company) {
  const prompt = `You are an expert B2B sales strategist. Analyze this company as a potential client and provide strategic insights. Return ONLY valid JSON with keys: fit_score (1-100), industry_analysis (string ~30 words), pain_points (array of 3 likely IT/tech pain points), approach_strategy (string ~40 words), talking_points (array of 3 strings), competitor_risk (low/medium/high).

Company Data:
- Name: ${company.name || 'Unknown'}
- Domain: ${company.domain || 'Unknown'}
- Industry: ${company.industry || 'Unknown'}
- Size: ${company.estimated_num_employees || 'Unknown'} employees
- Location: ${company.raw_address || 'Unknown'}
- Revenue: ${company.annual_revenue_printed || 'Unknown'}

Return valid JSON only, no markdown.`;

  return callClaude(prompt);
}

export async function generateCampaign(targetAudience, senderName = 'Lionel Mosley') {
  const prompt = `You are an expert email marketing strategist. Design a 3-email nurture sequence for GetResponse. Return ONLY valid JSON with keys: campaign_name (string), target_description (string ~20 words), emails (array of 3 objects each with: day (number), subject (string), preview_text (string ~15 words), body_outline (string ~40 words), cta_button (string ~5 words)).

Context:
- Business: IT Consulting by ${senderName}
- Target: ${targetAudience}
- Goal: Lead nurturing to conversion
- Platform: GetResponse

Return valid JSON only, no markdown.`;

  return callClaude(prompt);
}
